import React, { useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Form,
  InputGroup,
  Modal,
  Toast,
  ToastContainer,
  Spinner,
  Pagination,
  Alert,
} from 'react-bootstrap';
import {
  Building,
  Search,
  PlusCircle,
  Pencil,
  Trash,
  CheckCircle,
  XCircle,
  InfoCircle,
  Hospital,
  Shop,
  ChevronDown,
  ChevronUp,
} from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/AuthContext';

// Données fictives des structures
const mockStructures = [
  { id: 1, name: 'CHU Libreville', type: 'Hôpital', status: 'active', address: 'Libreville, Gabon', phone: '+241 01 23 45 67' },
  { id: 2, name: 'Pharmacie Centrale', type: 'Pharmacie', status: 'active', address: 'Libreville, Gabon', phone: '+241 01 23 45 68' },
  { id: 3, name: 'Clinique Ambroise Paré', type: 'Clinique', status: 'active', address: 'Port-Gentil, Gabon', phone: '+241 01 23 45 69' },
  { id: 4, name: 'Pharmacie Moderne', type: 'Pharmacie', status: 'inactive', address: 'Franceville, Gabon', phone: '+241 01 23 45 70' },
  { id: 5, name: 'Hôpital Régional de Franceville', type: 'Hôpital', status: 'active', address: 'Franceville, Gabon', phone: '+241 01 23 45 71' },
  { id: 6, name: 'Pharmacie du Quartier', type: 'Pharmacie', status: 'active', address: 'Oyem, Gabon', phone: '+241 01 23 45 72' },
  { id: 7, name: 'Clinique les Flamboyants', type: 'Clinique', status: 'inactive', address: 'Libreville, Gabon', phone: '+241 01 23 45 73' },
  { id: 8, name: 'Hôpital de Port-Gentil', type: 'Hôpital', status: 'active', address: 'Port-Gentil, Gabon', phone: '+241 01 23 45 74' },
];

const StructureManagement = () => {
  const { user } = useAuth();
  const [structures, setStructures] = useState(mockStructures);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'Hôpital',
    address: '',
    phone: '',
  });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedRows(newSet);
  };

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  };

  // Filtrage
  const filteredStructures = useMemo(() => {
    let filtered = structures;
    if (typeFilter !== 'all') {
      filtered = filtered.filter(s => s.type === typeFilter);
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s =>
        s.name.toLowerCase().includes(term) ||
        s.address.toLowerCase().includes(term) ||
        s.phone.includes(term)
      );
    }
    return filtered;
  }, [structures, searchTerm, typeFilter, statusFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredStructures.length / itemsPerPage);
  const paginatedStructures = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredStructures.slice(start, start + itemsPerPage);
  }, [filteredStructures, currentPage, itemsPerPage]);

  // Statistiques
  const stats = {
    total: structures.length,
    hospitals: structures.filter(s => s.type === 'Hôpital').length,
    pharmacies: structures.filter(s => s.type === 'Pharmacie').length,
    clinics: structures.filter(s => s.type === 'Clinique').length,
    active: structures.filter(s => s.status === 'active').length,
    inactive: structures.filter(s => s.status === 'inactive').length,
  };

  const statsCards = [
    { title: 'Total structures', value: stats.total, icon: Building, color: '#4361ee', gradient: 'linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)', bg: 'white' },
    { title: 'Hôpitaux', value: stats.hospitals, icon: Hospital, color: '#10b981', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', bg: 'white' },
    { title: 'Pharmacies', value: stats.pharmacies, icon: Shop, color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', bg: 'white' },
    { title: 'Actives', value: stats.active, icon: CheckCircle, color: '#06b6d4', gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', bg: 'white' },
  ];

  const getStatusBadge = (status) => {
    if (status === 'active') return <Badge bg="success" className="rounded-pill px-3 py-2"><CheckCircle size={12} className="me-1" /> Actif</Badge>;
    return <Badge bg="secondary" className="rounded-pill px-3 py-2"><XCircle size={12} className="me-1" /> Inactif</Badge>;
  };

  const handleAdd = () => {
    setFormData({ name: '', type: 'Hôpital', address: '', phone: '' });
    setShowAddModal(true);
  };

  const handleEdit = (structure) => {
    setSelectedStructure(structure);
    setFormData({
      name: structure.name,
      type: structure.type,
      address: structure.address,
      phone: structure.phone,
    });
    setShowEditModal(true);
  };

  const handleToggleStatus = (structure) => {
    const newStatus = structure.status === 'active' ? 'inactive' : 'active';
    setStructures(prev =>
      prev.map(s =>
        s.id === structure.id ? { ...s, status: newStatus } : s
      )
    );
    showToast(`Structure ${structure.name} ${newStatus === 'active' ? 'activée' : 'désactivée'}`, 'success');
  };

  const handleSaveAdd = () => {
    if (!formData.name.trim()) {
      showToast('Veuillez saisir un nom', 'danger');
      return;
    }
    setLoading(true);
    setTimeout(() => {
      const newId = Math.max(...structures.map(s => s.id), 0) + 1;
      const newStructure = {
        id: newId,
        name: formData.name,
        type: formData.type,
        status: 'active',
        address: formData.address,
        phone: formData.phone,
      };
      setStructures(prev => [...prev, newStructure]);
      showToast('Structure ajoutée avec succès', 'success');
      setShowAddModal(false);
      setLoading(false);
    }, 800);
  };

  const handleSaveEdit = () => {
    if (!formData.name.trim()) {
      showToast('Veuillez saisir un nom', 'danger');
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setStructures(prev =>
        prev.map(s =>
          s.id === selectedStructure.id
            ? { ...s, name: formData.name, type: formData.type, address: formData.address, phone: formData.phone }
            : s
        )
      );
      showToast('Structure modifiée avec succès', 'success');
      setShowEditModal(false);
      setLoading(false);
    }, 800);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const items = [];
    for (let i = 1; i <= totalPages; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
          {i}
        </Pagination.Item>
      );
    }
    return (
      <div className="d-flex justify-content-end mt-3">
        <Pagination size="sm">
          <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
          {items}
          <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
        </Pagination>
      </div>
    );
  };

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        <Toast show={toast.show} onClose={() => setToast(prev => ({ ...prev, show: false }))} bg={toast.variant} delay={4000} autohide>
          <Toast.Header closeButton={false}>
            <strong className="me-auto">
              {toast.variant === 'success' ? 'Succès' : toast.variant === 'danger' ? 'Erreur' : 'Information'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="container-fluid">
        {/* En-tête */}
        <div className="mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h4 className="main-title mb-0 d-flex align-items-center gap-2">
              <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                <Building size={24} className="text-primary" />
              </div>
              Gestion des structures
            </h4>
            <p className="text-muted mb-0 mt-1">
              Gérez les hôpitaux, cliniques et pharmacies partenaires
            </p>
          </div>
          <Button variant="primary" onClick={handleAdd} className="rounded-pill px-4">
            <PlusCircle size={16} className="me-1" /> Ajouter une structure
          </Button>
        </div>

        {/* Cartes de statistiques */}
        <Row className="g-3 mb-4">
          {statsCards.map((card, idx) => (
            <Col md={6} lg={3} xs={6} key={idx}>
              <Card className="border-0 shadow-sm stats-card h-100" style={{ borderRadius: '16px', background: card.bg }}>
                <div className="card-gradient" style={{ background: card.gradient, height: '4px' }}></div>
                <Card.Body className="p-3 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="text-muted mb-1 small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                        {card.title}
                      </h6>
                      <h3 className="mb-0 fw-bold" style={{ fontSize: '1.6rem' }}>
                        {card.value}
                      </h3>
                    </div>
                    <div className="icon-container rounded-3 d-flex align-items-center justify-content-center" style={{ background: `${card.color}20`, width: '48px', height: '48px' }}>
                      <card.icon size={28} style={{ color: card.color }} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Barre de recherche et filtres */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4 bg-white">
            <Row className="align-items-center g-3">
              <Col md={5} xs={6}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-0">
                    <Search size={18} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Rechercher par nom, adresse ou téléphone..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="border-0 bg-light"
                  />
                </InputGroup>
              </Col>
              <Col md={3} xs={6}>
                <Form.Select
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="border-0 bg-light"
                >
                  <option value="all">Tous types</option>
                  <option value="Hôpital">Hôpitaux</option>
                  <option value="Pharmacie">Pharmacies</option>
                  <option value="Clinique">Cliniques</option>
                </Form.Select>
              </Col>
              <Col md={2} xs={6}>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="border-0 bg-light"
                >
                  <option value="all">Tous statuts</option>
                  <option value="active">Actifs</option>
                  <option value="inactive">Inactifs</option>
                </Form.Select>
              </Col>
              <Col md={2} xs={6}>
                <Form.Select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border-0 bg-light"
                >
                  <option value={5}>5 par page</option>
                  <option value={10}>10 par page</option>
                  <option value={20}>20 par page</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tableau des structures */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
          <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
            <h5 className="fw-semibold mb-0">Liste des structures</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="bg-light">
                  <tr>
                    <th>Nom</th>
                    <th>Type</th>
                    <th className="d-none d-sm-table-cell">Adresse</th>
                    <th className="d-none d-sm-table-cell">Téléphone</th>
                    <th className="d-none d-sm-table-cell">Statut</th>
                    <th className="d-none d-sm-table-cell">Actions</th>
                    <th className="d-sm-none">Détails</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedStructures.length > 0 ? (
                    paginatedStructures.map(structure => {
                      const isExpanded = expandedRows.has(structure.id);
                      return (
                        <React.Fragment key={structure.id}>
                          {/* Ligne principale */}
                          <tr>
                            <td data-label="Nom" className="fw-medium">{structure.name}</td>
                            <td data-label="Type">
                              {structure.type === 'Hôpital' && <Hospital size={14} className="me-1" />}
                              {structure.type === 'Pharmacie' && <Shop size={14} className="me-1" />}
                              {structure.type === 'Clinique' && <Building size={14} className="me-1" />}
                              {structure.type}
                            </td>
                            <td data-label="Adresse" className="d-none d-sm-table-cell">{structure.address}</td>
                            <td data-label="Téléphone" className="d-none d-sm-table-cell">{structure.phone}</td>
                            <td data-label="Statut" className="d-none d-sm-table-cell">{getStatusBadge(structure.status)}</td>
                            <td data-label="Actions" className="d-none d-sm-table-cell">
                              <div className="d-flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                  style={{ width: '32px', height: '32px' }}
                                  onClick={() => handleEdit(structure)}
                                  title="Modifier"
                                >
                                  <Pencil size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant={structure.status === 'active' ? 'outline-secondary' : 'outline-success'}
                                  className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                  style={{ width: '32px', height: '32px' }}
                                  onClick={() => handleToggleStatus(structure)}
                                  title={structure.status === 'active' ? 'Désactiver' : 'Activer'}
                                >
                                  {structure.status === 'active' ? <XCircle size={14} /> : <CheckCircle size={14} />}
                                </Button>
                              </div>
                            </td>
                            <td className="d-sm-none text-center" style={{ verticalAlign: 'middle' }}>
                              <Button
                                variant="link"
                                onClick={() => toggleRow(structure.id)}
                                className="p-0 text-secondary"
                                style={{ textDecoration: 'none' }}
                              >
                                {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                              </Button>
                            </td>
                          </tr>
                          {/* Ligne de détails (visible seulement sur mobile et si dépliée) */}
                          {isExpanded && (
                            <tr className="d-sm-none detail-row">
                              <td colSpan="7" className="p-3 bg-light">
                                <div className="row g-2">
                                  <div className="col-6"><strong>Adresse :</strong> {structure.address}</div>
                                  <div className="col-6"><strong>Téléphone :</strong> {structure.phone}</div>
                                  <div className="col-6"><strong>Statut :</strong> {getStatusBadge(structure.status)}</div>
                                  <div className="col-12">
                                    <strong>Actions :</strong>
                                    <div className="d-flex gap-2 mt-1">
                                      <Button size="sm" variant="outline-primary" className="rounded-circle" onClick={() => handleEdit(structure)} title="Modifier">
                                        <Pencil size={14} />
                                      </Button>
                                      <Button size="sm" variant={structure.status === 'active' ? 'outline-secondary' : 'outline-success'} className="rounded-circle" onClick={() => handleToggleStatus(structure)} title={structure.status === 'active' ? 'Désactiver' : 'Activer'}>
                                        {structure.status === 'active' ? <XCircle size={14} /> : <CheckCircle size={14} />}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-5">
                        <Building size={48} className="text-muted mb-3" />
                        <p className="text-muted">Aucune structure trouvée</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            {renderPagination()}
          </Card.Body>
        </Card>
<br></br><br></br>
        {/* Modals (inchangés) */}
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title className="d-flex align-items-center gap-2"><PlusCircle size={20} /> Ajouter une structure</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3"><Form.Label>Nom <span className="text-danger">*</span></Form.Label><Form.Control type="text" placeholder="Nom de la structure" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Type</Form.Label><Form.Select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}><option value="Hôpital">Hôpital</option><option value="Pharmacie">Pharmacie</option><option value="Clinique">Clinique</option></Form.Select></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Adresse</Form.Label><Form.Control type="text" placeholder="Adresse" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Téléphone</Form.Label><Form.Control type="text" placeholder="Téléphone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)} className="rounded-pill">Annuler</Button>
            <Button variant="primary" onClick={handleSaveAdd} disabled={loading} className="rounded-pill px-4">
              {loading ? <Spinner size="sm" className="me-1" /> : <CheckCircle size={16} className="me-1" />}
              {loading ? 'Ajout...' : 'Ajouter'}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
          <Modal.Header closeButton className="bg-warning text-dark">
            <Modal.Title className="d-flex align-items-center gap-2"><Pencil size={20} /> Modifier la structure</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3"><Form.Label>Nom <span className="text-danger">*</span></Form.Label><Form.Control type="text" placeholder="Nom de la structure" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Type</Form.Label><Form.Select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}><option value="Hôpital">Hôpital</option><option value="Pharmacie">Pharmacie</option><option value="Clinique">Clinique</option></Form.Select></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Adresse</Form.Label><Form.Control type="text" placeholder="Adresse" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} /></Form.Group>
              <Form.Group className="mb-3"><Form.Label>Téléphone</Form.Label><Form.Control type="text" placeholder="Téléphone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)} className="rounded-pill">Annuler</Button>
            <Button variant="primary" onClick={handleSaveEdit} disabled={loading} className="rounded-pill px-4">
              {loading ? <Spinner size="sm" className="me-1" /> : <CheckCircle size={16} className="me-1" />}
              {loading ? 'Modification...' : 'Modifier'}
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 16px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
          }
          .card-gradient {
            transition: height 0.2s ease;
          }
          .stats-card:hover .card-gradient {
            height: 6px;
          }
          .icon-container {
            transition: transform 0.2s ease;
          }
          .stats-card:hover .icon-container {
            transform: scale(1.05);
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
            vertical-align: middle;
          }
          @media (max-width: 767.98px) {
            .table td, .table th {
              padding: 0.5rem;
            }
            .btn-sm {
              padding: 0.25rem 0.5rem;
              font-size: 0.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default StructureManagement;